/**
 * Real-time Subscription Hook for Admin Dashboard
 *
 * Enables live updates from Supabase for admin components.
 * Changes made in the mobile app will automatically reflect here.
 */

import { useEffect, useCallback, useRef } from 'react';
import { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

type ChangeEvent = 'INSERT' | 'UPDATE' | 'DELETE' | '*';

interface SubscriptionConfig {
  table: string;
  schema?: string;
  event?: ChangeEvent;
  filter?: string;
}

interface UseRealtimeSubscriptionOptions<T> {
  /** Supabase table to subscribe to */
  config: SubscriptionConfig | SubscriptionConfig[];
  /** Callback when data changes */
  onInsert?: (payload: T) => void;
  onUpdate?: (payload: { old: T; new: T }) => void;
  onDelete?: (payload: T) => void;
  /** Generic handler for any change */
  onChange?: (eventType: ChangeEvent, payload: any) => void;
  /** Whether subscription is enabled */
  enabled?: boolean;
}

/**
 * Hook to subscribe to real-time Supabase changes
 *
 * @example
 * // Subscribe to users table changes
 * useRealtimeSubscription({
 *   config: { table: 'users', event: '*' },
 *   onInsert: (user) => setUsers(prev => [user, ...prev]),
 *   onUpdate: ({ old, new: updated }) => setUsers(prev => prev.map(u => u.id === updated.id ? updated : u)),
 *   onDelete: (user) => setUsers(prev => prev.filter(u => u.id !== user.id))
 * });
 */
export function useRealtimeSubscription<T = any>({
  config,
  onInsert,
  onUpdate,
  onDelete,
  onChange,
  enabled = true
}: UseRealtimeSubscriptionOptions<T>) {
  const channelRef = useRef<RealtimeChannel | null>(null);

  const handleChange = useCallback((
    payload: RealtimePostgresChangesPayload<{ [key: string]: any }>
  ) => {
    const eventType = payload.eventType as ChangeEvent;

    console.log(`[Realtime] ${eventType} on ${payload.table}:`, payload);

    // Call generic onChange handler
    if (onChange) {
      onChange(eventType, payload);
    }

    // Call specific handlers
    switch (eventType) {
      case 'INSERT':
        if (onInsert) onInsert(payload.new as T);
        break;
      case 'UPDATE':
        if (onUpdate) onUpdate({ old: payload.old as T, new: payload.new as T });
        break;
      case 'DELETE':
        if (onDelete) onDelete(payload.old as T);
        break;
    }
  }, [onInsert, onUpdate, onDelete, onChange]);

  useEffect(() => {
    if (!enabled) {
      // Cleanup if disabled
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      return;
    }

    const configs = Array.isArray(config) ? config : [config];
    const channelName = `admin-realtime-${configs.map(c => c.table).join('-')}-${Date.now()}`;

    let channel = supabase.channel(channelName);

    // Add listeners for each config
    configs.forEach(cfg => {
      const { table, schema = 'public', event = '*', filter } = cfg;

      channel = channel.on(
        'postgres_changes',
        {
          event,
          schema,
          table,
          filter
        } as any,
        handleChange
      );
    });

    // Subscribe to the channel
    channel.subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        console.log(`[Realtime] Subscribed to: ${configs.map(c => c.table).join(', ')}`);
      } else if (status === 'CHANNEL_ERROR') {
        console.error('[Realtime] Subscription error');
      }
    });

    channelRef.current = channel;

    // Cleanup on unmount
    return () => {
      if (channelRef.current) {
        console.log(`[Realtime] Unsubscribing from: ${configs.map(c => c.table).join(', ')}`);
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [config, enabled, handleChange]);

  // Return a function to manually unsubscribe
  const unsubscribe = useCallback(() => {
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
  }, []);

  return { unsubscribe };
}

/**
 * Hook to subscribe to multiple tables at once
 */
export function useMultiTableSubscription(
  tables: string[],
  onAnyChange: (table: string, eventType: ChangeEvent, payload: any) => void,
  enabled = true
) {
  return useRealtimeSubscription({
    config: tables.map(table => ({ table, event: '*' as ChangeEvent })),
    onChange: (eventType, payload) => {
      onAnyChange(payload.table, eventType, payload);
    },
    enabled
  });
}

/**
 * Hook specifically for dashboard stats - subscribes to key tables
 */
export function useDashboardRealtimeStats(
  onStatsChange: () => void,
  enabled = true
) {
  return useRealtimeSubscription({
    config: [
      { table: 'users', event: '*' },
      { table: 'posts', event: '*' },
      { table: 'talent_videos', event: '*' },
      { table: 'events', event: '*' },
      { table: 'event_submissions', event: '*' }
    ],
    onChange: () => {
      // Debounce the refresh to avoid rapid updates
      onStatsChange();
    },
    enabled
  });
}

export default useRealtimeSubscription;
